import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AppShell } from '@/app/AppShell';
import { AuthProvider } from '@/app/auth';
import { FlightAccessProvider } from '@/app/flightAccess';
import { RequireAuth, RequireRole } from '@/app/RequireAuth';
import { MAP_PATH, SCHEDULE_PATH } from '@/app/routes';
import { ThemeProvider } from '@/app/theme';
import { I18nProvider } from '@/i18n';
import { AirlinesPage } from '@/features/airlines/AirlinesPage';
import { AirportsPage } from '@/features/airports/AirportsPage';
import { InternationalArrivalsPage } from '@/features/international-arrivals/InternationalArrivalsPage';
import { LoginPage } from '@/features/auth/LoginPage';
import { MonitorPage } from '@/features/monitor/MonitorPage';
import { SchedulePage } from '@/features/schedule/SchedulePage';
import { StyleGuidePage } from '@/features/styleguide/StyleGuidePage';
import { ProfilePage } from '@/features/users/ProfilePage';
import { UsersPage } from '@/features/users/UsersPage';
import {
  ROLES_ADMIN_ONLY,
  ROLES_INTERNATIONAL_ARRIVALS,
  ROLES_MAP,
  ROLES_ONLINE_TABLE,
} from '@/types/user';

/**
 * The one screen loaded on first visit rather than with the app: the map library and
 * the country outlines are most of a megabyte, and the board — where everyone lands
 * and most people stay — has no use for any of it.
 */
const FlightMapPage = lazy(() =>
  import('@/features/map/FlightMapPage').then((module) => ({ default: module.FlightMapPage })),
);

/**
 * The app shell: providers outside the router, routes inside.
 *
 * Provider order is deliberate. The theme is a property of the document and has to
 * be applied before the first paint of any route. Language wraps auth because the
 * login screen has to be readable before anyone is signed in. The flight access sits
 * inside auth because it is read for the session's user and dropped with the session.
 * The router sits inside all of them so that a guard can redirect.
 */
const App = () => (
  <ThemeProvider>
    <I18nProvider>
      <AuthProvider>
        <FlightAccessProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<LoginPage />} />

              {/* Everything below needs a session. The guard wraps the shell rather
                  than each page, so the header cannot render for a signed-out
                  reader. */}
              <Route
                element={
                  <RequireAuth>
                    <AppShell />
                  </RequireAuth>
                }
              >
                {/* The operations table is what this app is for, and it is the only
                    screen a non-administrator has — so it is where everyone lands. It
                    is named rather than sitting on `/`, because a bare `/` cannot be
                    linked to, bookmarked or spoken about as a place. */}
                {/*
                  Guarded, though it is the landing screen — because `SecurityConfig`
                  does not name INTEGRATOR or OPERATOR on the online table, so those
                  two roles get a 403 from every request the board makes.

                  The session page used to be where they learned that. Without the
                  guard they would now land on a board of filters above a permanently
                  empty table: the guard turns that into one sentence saying the screen
                  is not theirs, which is the truth and the whole truth for them.
                */}
                <Route
                  path={SCHEDULE_PATH}
                  element={
                    <RequireRole roles={ROLES_ONLINE_TABLE}>
                      <SchedulePage />
                    </RequireRole>
                  }
                />

                {/* The board's roles, and a wall screen: the map reads the same API,
                    under `/online-table/airborne`, and is refused to the same two
                    roles. A DISPLAY account has this and nothing else — the shell
                    sends it here from anywhere. */}
                <Route
                  path={MAP_PATH}
                  element={
                    <RequireRole roles={ROLES_MAP}>
                      <Suspense fallback={null}>
                        <FlightMapPage />
                      </Suspense>
                    </RequireRole>
                  }
                />

                {/*
                  ADMIN and AAK. Narrower than the board on purpose, and the guard is
                  not a nicety: this API refuses USER and KGA_USER on its READ endpoint
                  too, so without it those two roles open a journal whose every
                  request answers 403.
                */}
                <Route
                  path="/international-arrivals"
                  element={
                    <RequireRole roles={ROLES_INTERNATIONAL_ARRIVALS}>
                      <InternationalArrivalsPage />
                    </RequireRole>
                  }
                />

                {/* No role guard: `/user/me` is the one part of the user API every
                    signed-in role may call, and it can only ever reach the caller's own
                    account — the id comes from the token. */}
                <Route path="/profile" element={<ProfilePage />} />

                {/*
                  ADMINISTRATORS ONLY, all four of them.

                  The dictionaries are a deliberate exception to what the server
                  permits: `/airport/**` and `/airline/**` are READABLE by AAK, USER
                  and KGA_USER, and this app does not offer them. Everyone but an
                  administrator works the board and nothing else, so a dictionary they
                  cannot change is a screen they have no use for.

                  Restricting the UI beyond the server is always safe — the reverse
                  never is. `ROLES_DICTIONARY_READ` still records what the server
                  allows, in case that decision is revisited.
                */}
                <Route
                  path="/airports"
                  element={
                    <RequireRole roles={ROLES_ADMIN_ONLY}>
                      <AirportsPage />
                    </RequireRole>
                  }
                />

                <Route
                  path="/airlines"
                  element={
                    <RequireRole roles={ROLES_ADMIN_ONLY}>
                      <AirlinesPage />
                    </RequireRole>
                  }
                />

                {/* ADMIN only, GETs included: the monitor reads operational internals
                    and its settings PUT changes what the feeds may write. */}
                <Route
                  path="/monitor"
                  element={
                    <RequireRole roles={ROLES_ADMIN_ONLY}>
                      <MonitorPage />
                    </RequireRole>
                  }
                />

                {/* Every call this screen makes is ADMIN-only, so the guard is not a
                    nicety: without it another role opens a page whose every request
                    answers 403. The server enforces it regardless. */}
                <Route
                  path="/users"
                  element={
                    <RequireRole roles={ROLES_ADMIN_ONLY}>
                      <UsersPage />
                    </RequireRole>
                  }
                />

                {/* Developer-facing, so it sits behind the one role that has no
                    business being surprised by it. */}
                <Route
                  path="/styleguide"
                  element={
                    <RequireRole roles={ROLES_ADMIN_ONLY}>
                      <StyleGuidePage />
                    </RequireRole>
                  }
                />
              </Route>

              {/* `/` is not a screen, it is an entrance: it and any unknown path send
                  the reader to the board — and a signed-out one on to login, by way of
                  the guard on the shell. `replace`, so Back does not bounce off the
                  redirect straight back into it. */}
              <Route path="*" element={<Navigate to={SCHEDULE_PATH} replace />} />
            </Routes>
          </BrowserRouter>
        </FlightAccessProvider>
      </AuthProvider>
    </I18nProvider>
  </ThemeProvider>
);

export default App;
